import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectOption } from '../components/ui/select';
import { DatePicker } from '../components/ui/date-picker';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, error: authError } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    department: '',
    joinDate: new Date(),
    birthdate: null
  });
  
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const departments = [
    'Admin',
    'Reception',
    'Storeroom',
    'Reps / Sales',
    'IT',
    'Internal Admin',
    'Technician'
  ];
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleDateChange = (name, date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Format dates
      const userData = {
        ...formData,
        joinDate: formData.joinDate ? formData.joinDate.toISOString().split('T')[0] : null,
        birthdate: formData.birthdate ? formData.birthdate.toISOString().split('T')[0] : null
      };
      
      // Remove confirmPassword
      delete userData.confirmPassword;
      
      await register(userData);
      // Navigate back to the staff management page instead of home
      navigate('/staff', { state: { refresh: true } });
    } catch (err) {
      const errorMessage = 
        err.response?.data?.message || 
        authError || 
        'Registration failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to staff management page without saving
    navigate('/staff');
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Add New Employee</CardTitle>
          <CardDescription className="text-center">
            Enter employee information to create an account
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {error && (
            <div className="bg-red-50 text-red-500 p-3 rounded-md mb-4">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="name">
                Full Name
              </label>
              <Input
                id="name"
                name="name"
                placeholder="Full name"
                required
                value={formData.name}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="email">
                Email
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Email address"
                required
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="password">
                  Password
                </label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="confirmPassword">
                  Confirm Password
                </label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="department">
                Department
              </label>
              <Select
                id="department"
                name="department"
                required
                value={formData.department}
                onChange={handleChange}
                disabled={isLoading}
              >
                <SelectOption value="" disabled>Select department</SelectOption>
                {departments.map(dept => (
                  <SelectOption key={dept} value={dept}>{dept}</SelectOption>
                ))}
              </Select>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Join Date
                </label>
                <DatePicker
                  date={formData.joinDate}
                  setDate={(date) => handleDateChange('joinDate', date)}
                  disabled={isLoading}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Birthdate (Optional)
                </label>
                <DatePicker
                  date={formData.birthdate}
                  setDate={(date) => handleDateChange('birthdate', date)}
                  placeholder="Select birthdate"
                  disabled={isLoading}
                />
              </div>
            </div>
            
            <div className="flex space-x-4">
              <Button
                type="submit"
                className="flex-1"
                disabled={isLoading}
              >
                {isLoading ? 'Creating account...' : 'Add Employee'}
              </Button>
              
              <Button 
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
        
       
      </Card>
    </div>
  );
};

export default RegisterPage;